/**
 * PublicSubmit — chekste.com/submit/:username
 *
 * The form a wholesaler, agent or seller fills to drop a property into an investor's
 * inbox. Public on purpose (the sender has no account); the recipient is the handle in
 * the URL, resolved to the owner's DISPLAY NAME through /inbox/{handle}/recipient — so
 * the page says "Send a deal to Nelson Salgado", never "@investor14d75a87".
 *
 * The sender's contact boxes are what the investor will see as "submitted by". They are
 * labelled, and the browser is told not to autofill them: a saved address filled into an
 * unlabelled "Email" box is how a deal arrived attributed to the wrong person.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { inboxAPI } from '../api';
import { useTranslation } from '../i18n';

const EMPTY = {
  input_value: '',
  property_url: '',
  input_type: 'address',
  price: '',
  beds: '',
  baths: '',
  sqft: '',
  notes: '',
  submitted_by_name: '',
  submitted_by_email: '',
  submitted_by_phone: '',
};

const T = {
  en: {
    title: 'Submit a Deal',
    sendTo: (name) => <>Send a property deal to <span className="font-semibold text-secondary" data-testid="recipient-name">{name}</span></>,
    notFound: 'This inbox link is no longer valid. Ask the investor for their current link.',
    address: 'Address', url: 'Property URL',
    labelUrl: 'Property Listing URL *', labelAddr: 'Property Address or URL *',
    phUrl: 'https://www.zillow.com/homedetails/...', phAddr: '123 Main St, Detroit, MI 48226 or paste URL',
    supports: 'Supports Zillow, Redfin, Realtor.com, Trulia, and Homes.com links',
    price: 'Asking Price', beds: 'Beds', baths: 'Baths', sqft: 'Sqft',
    notes: 'Notes / Comments', phNotes: 'Any additional details about the property, condition, motivation...',
    contact: 'Your contact information', contactHint: (name) => `This is what ${name} will see as the sender. Type it in — do not rely on autofill.`,
    yourName: 'Your name', yourEmail: 'Your email', yourPhone: 'Your phone (optional)',
    submit: 'Submit Deal', submitting: 'Submitting...',
    missing: 'Please enter a property address or URL',
    failed: 'Failed to submit deal. Please try again.',
    doneTitle: 'Deal Submitted!', done: (name) => `Your deal has been sent to ${name} for review.`,
    another: 'Submit Another Deal', powered: 'Powered by',
  },
  es: {
    title: 'Enviar un Trato',
    sendTo: (name) => <>Envía una propiedad a <span className="font-semibold text-secondary" data-testid="recipient-name">{name}</span></>,
    notFound: 'Este enlace de buzón ya no es válido. Pídele al inversionista su enlace actual.',
    address: 'Dirección', url: 'URL de la propiedad',
    labelUrl: 'URL del anuncio *', labelAddr: 'Dirección o URL de la propiedad *',
    phUrl: 'https://www.zillow.com/homedetails/...', phAddr: '123 Main St, Detroit, MI 48226 o pega una URL',
    supports: 'Acepta enlaces de Zillow, Redfin, Realtor.com, Trulia y Homes.com',
    price: 'Precio pedido', beds: 'Habitaciones', baths: 'Baños', sqft: 'Pies²',
    notes: 'Notas / Comentarios', phNotes: 'Detalles adicionales: condición, motivación del vendedor...',
    contact: 'Tus datos de contacto', contactHint: (name) => `Esto es lo que ${name} verá como remitente. Escríbelo tú — no confíes en el autorrelleno.`,
    yourName: 'Tu nombre', yourEmail: 'Tu email', yourPhone: 'Tu teléfono (opcional)',
    submit: 'Enviar Trato', submitting: 'Enviando...',
    missing: 'Ingresa una dirección o URL de la propiedad',
    failed: 'No se pudo enviar el trato. Inténtalo de nuevo.',
    doneTitle: '¡Trato enviado!', done: (name) => `Tu trato fue enviado a ${name} para su revisión.`,
    another: 'Enviar otro trato', powered: 'Con la tecnología de',
  },
};

const isUrl = (text) => /^(https?:\/\/)?(www\.)?(zillow|redfin|realtor|trulia|homes)\.(com|co)/i.test(text);

const PublicSubmit = () => {
  const { username } = useParams();
  const { lang, setLanguage } = useTranslation();
  const t = T[lang === 'es' ? 'es' : 'en'];
  const [recipient, setRecipient] = useState(null);      // { display_name } | null (loading) | false (404)
  const [formData, setFormData] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [inputMode, setInputMode] = useState('address');

  useEffect(() => {
    let alive = true;
    inboxAPI.getRecipient(username)
      .then((r) => { if (alive) setRecipient(r.data); })
      .catch(() => { if (alive) setRecipient(false); });
    return () => { alive = false; };
  }, [username]);

  const name = recipient ? recipient.display_name : 'CHEKSTE';

  const handleInputChange = (value) => {
    setFormData({ ...formData, input_value: value });
    if (isUrl(value)) {
      setInputMode('url');
      setFormData((prev) => ({ ...prev, input_value: value, property_url: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.input_value.trim() && !formData.property_url.trim()) {
      setError(t.missing);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await inboxAPI.submit(username, {
        ...formData,
        input_type: inputMode === 'url' ? 'url' : 'address',
        price: formData.price ? parseFloat(formData.price) : null,
        beds: formData.beds ? parseInt(formData.beds, 10) : null,
        baths: formData.baths ? parseFloat(formData.baths) : null,
        sqft: formData.sqft ? parseInt(formData.sqft, 10) : null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || t.failed);
    } finally {
      setSubmitting(false);
    }
  };

  const langSwitch = (
    <div className="flex justify-center gap-2 text-xs mb-4" data-testid="submit-lang-switch">
      {['en', 'es'].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLanguage(l)}
          className={`px-2 py-1 rounded ${lang === l ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-800'}`}
        >
          {l === 'en' ? 'English' : 'Español'}
        </button>
      ))}
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center" data-testid="submit-done">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2">{t.doneTitle}</h1>
          <p className="text-muted-foreground mb-6">{t.done(name)}</p>
          <button
            onClick={() => { setSubmitted(false); setFormData(EMPTY); setInputMode('address'); }}
            className="px-6 py-3 bg-secondary text-white rounded-lg font-semibold hover:bg-blue-700 transition-all"
          >
            {t.another}
          </button>
        </div>
      </div>
    );
  }

  const field = 'w-full px-4 py-3 border-2 border-border rounded-lg focus:outline-none focus:border-secondary transition-colors';
  const small = 'w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-secondary transition-colors text-sm';

  return (
    <div className="min-h-screen bg-background py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-lg mx-auto">
        {langSwitch}
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">{t.title}</h1>
          {recipient === false ? (
            <p className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm" data-testid="recipient-missing">{t.notFound}</p>
          ) : (
            <p className="text-muted-foreground">{t.sendTo(name)}</p>
          )}
        </div>

        {recipient !== false && (
          <div className="bg-white rounded-2xl shadow-lg border border-border p-6 sm:p-8">
            {/* autoComplete="off" on the form plus non-standard field names below: Chrome
                ignores "off" on anything it recognises as an email/name field, so the
                sender boxes deliberately carry names it does not recognise. */}
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" autoComplete="off" data-testid="submit-form">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="submit-error">{error}</div>
              )}

              <div className="flex gap-2 mb-4">
                {[['address', t.address], ['url', t.url]].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setInputMode(mode)}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                      inputMode === mode ? 'bg-secondary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{inputMode === 'url' ? t.labelUrl : t.labelAddr}</label>
                <input
                  type="text"
                  value={formData.input_value}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder={inputMode === 'url' ? t.phUrl : t.phAddr}
                  className={field}
                  autoComplete="off"
                  data-testid="submit-address"
                  required
                />
                {inputMode === 'url' && <p className="text-xs text-muted-foreground mt-1">{t.supports}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t.price}</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="150000"
                    className={`${field} pl-8`}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[['beds', t.beds, '3', '1'], ['baths', t.baths, '2', '0.5'], ['sqft', t.sqft, '1500', '1']].map(([k, label, ph, step]) => (
                  <div key={k}>
                    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
                    <input
                      type="number"
                      step={step}
                      min="0"
                      value={formData[k]}
                      onChange={(e) => setFormData({ ...formData, [k]: e.target.value })}
                      placeholder={ph}
                      className={small}
                      autoComplete="off"
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t.notes}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t.phNotes}
                  rows={3}
                  className={`${field} resize-none`}
                  autoComplete="off"
                />
              </div>

              <div className="p-4 bg-slate-50 rounded-lg" data-testid="sender-block">
                <p className="text-sm font-medium text-slate-700 mb-1">{t.contact}</p>
                <p className="text-xs text-slate-500 mb-3">{t.contactHint(name)}</p>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label htmlFor="sender-name" className="block text-xs font-medium text-slate-600 mb-1">{t.yourName}</label>
                    <input
                      id="sender-name"
                      name="deal_sender_alias"
                      type="text"
                      value={formData.submitted_by_name}
                      onChange={(e) => setFormData({ ...formData, submitted_by_name: e.target.value })}
                      className={small}
                      autoComplete="off"
                      data-testid="sender-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="sender-email" className="block text-xs font-medium text-slate-600 mb-1">{t.yourEmail}</label>
                      <input
                        id="sender-email"
                        name="deal_sender_contact"
                        type="text"
                        inputMode="email"
                        value={formData.submitted_by_email}
                        onChange={(e) => setFormData({ ...formData, submitted_by_email: e.target.value })}
                        className={small}
                        autoComplete="off"
                        data-testid="sender-email"
                      />
                    </div>
                    <div>
                      <label htmlFor="sender-phone" className="block text-xs font-medium text-slate-600 mb-1">{t.yourPhone}</label>
                      <input
                        id="sender-phone"
                        name="deal_sender_line"
                        type="text"
                        inputMode="tel"
                        value={formData.submitted_by_phone}
                        onChange={(e) => setFormData({ ...formData, submitted_by_phone: e.target.value })}
                        className={small}
                        autoComplete="off"
                        data-testid="sender-phone"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-4 bg-secondary text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                data-testid="submit-deal-btn"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t.submitting}
                  </>
                ) : t.submit}
              </button>
            </form>
          </div>
        )}

        <p className="text-center text-sm text-muted-foreground mt-6">
          {t.powered} <span className="font-semibold text-secondary">CHEKSTE</span>
        </p>
      </div>
    </div>
  );
};

export default PublicSubmit;
